import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { BannerItem } from '@/mocks/types';
import { bannerComics } from '@/mocks/comics';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % bannerComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const banner: BannerItem = bannerComics[current];

  return (
    <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
      {/* Background images */}
      {bannerComics.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            className="w-full h-full object-cover object-center"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />

      {/* Content */}
      <div className="absolute inset-0 flex items-end pb-16 md:pb-20">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
          <div
            key={banner.id}
            className="animate-[fadeInUp_0.5s_ease-out] max-w-lg"
          >
            <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium mb-4">
              热门推荐
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3 leading-tight">
              {banner.title}
            </h2>
            <p className="text-lg text-white/80 font-medium mb-2">
              {banner.subtitle}
            </p>
            <p className="text-sm text-white/60 leading-relaxed mb-6 line-clamp-2">
              {banner.description}
            </p>
            <Link
              to={`/comic/${banner.comicId}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap"
            >
              开始阅读
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 right-6 md:right-10 flex items-center gap-2">
        {bannerComics.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? 'bg-white w-8'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>

      {/* Arrows */}
      <button
        onClick={() => goTo((current - 1 + bannerComics.length) % bannerComics.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={() => goTo((current + 1) % bannerComics.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Animation keyframes injected via style tag */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}